import React, { useEffect, useRef, useState } from 'react';

import { isIgnorableMediaPlaybackError } from '../../utils/mediaPlaybackErrors';

interface CardVideoProps {
    url: string;
    option?: string;
    size?: string;
    className?: string;
    style?: React.CSSProperties;
    preload?: 'auto' | 'metadata' | 'none';
    loop?: boolean;
    controls?: boolean;
    width?: string | number;
    height?: string | number;
    onClick?: () => void;
    enableHoverPreview?: boolean;
}

const CardVideo: React.FC<CardVideoProps> = ({
    url,
    option = 'top',
    size,
    className = '',
    style = {},
    preload = 'metadata',
    loop = true,
    controls = false,
    width,
    height,
    onClick,
    enableHoverPreview = true,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const previewEnabledRef = useRef(enableHoverPreview);
    const urlRef = useRef(url);
    const hoveringRef = useRef(false);
    const isIntersectingRef = useRef(false);
    const [shouldLoad, setShouldLoad] = useState(false);
    previewEnabledRef.current = enableHoverPreview;
    urlRef.current = url;

    const playVideo = () => {
        if (!previewEnabledRef.current) return;
        const el = videoRef.current;
        if (!el || !el.paused) {
            return;
        }
        void el.play().catch((err: unknown) => {
            if (isIgnorableMediaPlaybackError(err)) {
                return;
            }
            if (process.env.NODE_ENV === 'development') {
                console.warn('[CardVideo] play() failed', err);
            }
        });
    };

    const pauseVideo = () => {
        const el = videoRef.current;
        if (!el || el.paused) {
            return;
        }
        try {
            el.pause();
        } catch {
            // pause() is sync; guard for environments that may throw
        }
    };

    const ensureSource = () => {
        const el = videoRef.current;
        const nextUrl = urlRef.current;
        if (!el || !nextUrl) return;
        if (el.getAttribute('src') !== nextUrl) {
            el.src = nextUrl;
        }
        setShouldLoad(true);
    };

    useEffect(() => {
        return () => {
            const el = videoRef.current;
            if (!el) {
                return;
            }
            try {
                el.pause();
            } catch {
                /* ignore */
            }
            el.removeAttribute('src');
            el.load();
        };
    }, []);

    useEffect(() => {
        if (!enableHoverPreview) {
            hoveringRef.current = false;
            pauseVideo();
        }
        if (isIntersectingRef.current) {
            setShouldLoad(true);
        }
    }, [enableHoverPreview]);

    useEffect(() => {
        const container = containerRef.current;
        if (!container || typeof IntersectionObserver === 'undefined') {
            return undefined;
        }

        const observer = new IntersectionObserver(entries => {
            const intersecting = entries.some(entry => entry.isIntersecting);
            isIntersectingRef.current = intersecting;
            if (intersecting) {
                setShouldLoad(true);
            }
        });
        observer.observe(container);

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        if (!containerRef.current) return undefined;

        const container = containerRef.current;
        const onEnter = () => {
            hoveringRef.current = true;
            if (!previewEnabledRef.current) return;
            ensureSource();
            playVideo();
        };
        const onLeave = () => {
            hoveringRef.current = false;
            pauseVideo();
        };
        container.addEventListener('mouseenter', onEnter);
        container.addEventListener('mouseleave', onLeave);

        return () => {
            container.removeEventListener('mouseenter', onEnter);
            container.removeEventListener('mouseleave', onLeave);
        };
    }, []);

    const baseClass = 'gds-card__img-container';
    const optionClass = option ? `${baseClass}--${option}` : '';
    const sizeClass = size ? `${baseClass}--${size}` : '';
    const classNames = `${baseClass} ${optionClass} ${sizeClass} ${className}`;

    return (
        <div
            ref={containerRef}
            className={classNames}
            onClick={onClick}
            style={style}
        >
            <video
                className="cover-video"
                ref={videoRef}
                src={shouldLoad ? url : undefined}
                preload={shouldLoad ? preload : 'none'}
                loop={loop}
                controls={controls}
                width={width}
                height={height}
                muted
            />
        </div>
    );
};

export default CardVideo;
